// beziehungsZiehen.ts — was passiert, wenn in der Firmenakte eine Zeile aus
// einer Liste in eine andere gezogen wird (Plan 2026-10-01 Verbindungen/
// Beziehungen, Paket A6, User 02.10.2026).
//
// Die vier Listen der Firmenakte/-anlage:
//   leitung       „Unternehmensleitung und Gesellschafter" (Menschen-Einträge
//                 und gespiegelte Firmen: Mutterfirma / Beteiligung an dieser)
//   verantwortung „Verantwortung fürs Unternehmen"
//   mensch        „Mensch im Unternehmen"
//   verbindung    „Verbindung zu Unternehmen" (Tochter, Schwester, Beteiligung, JV)
//
// Jede Ablage öffnet ein vorhandenes Popup mit Vorbelegung — gespeichert wird
// erst mit „Übernehmen" und dann wie jede andere Änderung beim Speichern des
// Formulars. Abbrechen ändert nichts. Was nicht passt, lehnt das Ziel schon
// beim Überfahren ab (`ablageFuer` → `nichtErlaubt`).
//
// Rein und ohne React — die Regeln sind per Vitest prüfbar.

import { type FirmaConnGruppe } from "../../data/firmenverbindungen";

/** Ziehart aller Listen der Firmenakte (MIME `application/x-kmvs-firmenakte`). */
export const ZIEH_ART_FIRMENAKTE = "firmenakte";

export type FirmenListe = "leitung" | "verantwortung" | "mensch" | "verbindung";

/** Die gezogene Zeile — als JSON in den Zieh-Daten. */
export type ZiehQuelle = {
  liste: FirmenListe;
  /** Woher der Eintrag stammt: Menschen-Karte oder Firmenverbindung. */
  eintrag: "mensch" | "firmaConn";
  /** Eintrags-id in `rel_menschen` bzw. `rel_firmaConn`. */
  id: string;
  gegenueber: "Person" | "Firma";
  /** Nur Firmenverbindung: Rolle und Quote. */
  role?: string;
  share?: string;
};

export type ZiehErgebnis =
  /** Eigene Liste — nichts tun, nichts anzeigen. */
  | { aktion: "keine" }
  | { aktion: "nichtErlaubt"; grund: string }
  /**
   * Popup „Verbindung zu einem Unternehmen". `ersetzen`: derselbe Eintrag wird
   * mit neuer Rolle bearbeitet (Übernehmen ersetzt ihn), sonst entsteht ein
   * zusätzlicher Eintrag mit derselben Firma.
   */
  /** `role` leer = der Anwender wählt die Art (Pflichtauswahl im Popup). */
  | { aktion: "firmaConn"; gruppe: FirmaConnGruppe | null; ersetzen: boolean; role: string; share?: string }
  /** Popup „Verantwortung fürs Unternehmen" für denselben Menschen-Eintrag. */
  | { aktion: "menschBearbeiten"; haken: boolean }
  /** Popup „Verantwortung fürs Unternehmen" neu, mit der Firma als Gegenüber. */
  | { aktion: "menschNeu" };

/** Eine Firmenverbindung, die als solche bestehen bleibt (keine Anteils-Aussage). */
const istSchwesterOderJv = (role?: string) =>
  role === "Schwester" || role === "Schwesterfirma" || role === "Joint Venture";

const KEINE_FIRMA_ALS_MENSCH = "Eine Firma ist kein Mensch im Unternehmen.";
const KEINE_PERSON_ALS_VERBINDUNG =
  "Personen gehören nicht zu den Firmenverbindungen — hier stehen nur Unternehmen.";

/**
 * Quelle × Ziel → was passiert. Die Regeln (User 02.10.2026):
 *   - Verbindung zu Unternehmen → Leitung/Gesellschafter: Popup mit den Rollen
 *     Mutterfirma / Beteiligung an dieser Firma; die Art wählt der Anwender
 *     (leer, Pflichtauswahl — keine Automatik nach der Quote, User 02.10.2026).
 *     Eine Schwesterfirma (oder ein Joint-Venture-Partner) bleibt, was sie ist —
 *     dazu kommt ein weiterer Eintrag mit Quote.
 *   - Mensch im Unternehmen ⇄ Verantwortung fürs Unternehmen: Popup desselben
 *     Eintrags; nach „Mensch im Unternehmen" mit gesetztem Haken.
 *   - Haushalt ⇄ weitere Beziehungen läuft unverändert in HouseholdList.
 */
export function ablageFuer(
  q: Pick<ZiehQuelle, "liste" | "eintrag" | "gegenueber" | "role" | "share">,
  ziel: FirmenListe,
): ZiehErgebnis {
  if (q.liste === ziel) return { aktion: "keine" };
  const firma = q.gegenueber === "Firma";

  switch (ziel) {
    case "leitung": {
      if (q.eintrag === "firmaConn") {
        if (istSchwesterOderJv(q.role)) {
          return { aktion: "firmaConn", gruppe: "halter", ersetzen: false, role: "" };
        }
        return { aktion: "firmaConn", gruppe: "halter", ersetzen: true, role: "", share: q.share };
      }
      return { aktion: "menschBearbeiten", haken: false };
    }
    case "verantwortung": {
      if (q.eintrag === "firmaConn") return { aktion: "menschNeu" };
      return { aktion: "menschBearbeiten", haken: false };
    }
    case "mensch": {
      if (firma) return { aktion: "nichtErlaubt", grund: KEINE_FIRMA_ALS_MENSCH };
      return { aktion: "menschBearbeiten", haken: true };
    }
    case "verbindung": {
      if (!firma) return { aktion: "nichtErlaubt", grund: KEINE_PERSON_ALS_VERBINDUNG };
      if (q.eintrag === "firmaConn") {
        // Aus der Gesellschaftertabelle (Mutterfirma / Beteiligung an dieser):
        // dieselbe Verbindung — die neue Art wählt der Anwender.
        return { aktion: "firmaConn", gruppe: null, ersetzen: true, role: "", share: q.share };
      }
      // Eine Firma aus der Verantwortungs-Karte (z. B. Steuerberater) bekommt
      // zusätzlich eine Firmenverbindung; ihre Verantwortung bleibt.
      return { aktion: "firmaConn", gruppe: null, ersetzen: false, role: "" };
    }
  }
}

// ── Kennzeichen beim Ziehen ─────────────────────────────────────────────────
// Beim Überfahren sind nur die MIME-TYPEN lesbar, nicht die Daten. Damit ein
// Ziel trotzdem schon dann „nicht erlaubt" zeigen kann, trägt die Zeile ihre
// Herkunft zusätzlich als Typen.

const MARKER = "fa-";

/** Kennzeichen einer gezogenen Zeile (je ein MIME-Suffix). */
export const ziehMarkerVon = (q: ZiehQuelle): string[] => [
  `${MARKER}von-${q.liste}`,
  `${MARKER}eintrag-${q.eintrag === "firmaConn" ? "firmaconn" : "mensch"}`,
  `${MARKER}typ-${q.gegenueber === "Firma" ? "firma" : "person"}`,
];

/** Zieh-Daten einer Zeile. */
export const ziehDatenVon = (q: ZiehQuelle): string => JSON.stringify(q);

/** Zieh-Daten zurücklesen — `null`, wenn sie nicht von hier stammen. */
export function ziehQuelleAus(daten: string): ZiehQuelle | null {
  try {
    const q = JSON.parse(daten) as Partial<ZiehQuelle>;
    if (!q || typeof q.id !== "string" || !q.liste || !q.eintrag) return null;
    return { gegenueber: "Person", ...q } as ZiehQuelle;
  } catch {
    return null;
  }
}

/** Herkunft aus den MIME-Typen (beim Überfahren). */
export function quelleAusTypen(
  typen: readonly string[],
): Pick<ZiehQuelle, "liste" | "eintrag" | "gegenueber"> | null {
  const wert = (art: string) => {
    const t = typen.find((x) => x.startsWith(`application/x-kmvs-${MARKER}${art}-`));
    return t ? t.slice(`application/x-kmvs-${MARKER}${art}-`.length) : null;
  };
  const liste = wert("von") as FirmenListe | null;
  const eintrag = wert("eintrag");
  const typ = wert("typ");
  if (!liste || !eintrag) return null;
  return {
    liste,
    eintrag: eintrag === "firmaconn" ? "firmaConn" : "mensch",
    gegenueber: typ === "firma" ? "Firma" : "Person",
  };
}

/** `pruefen` einer Ziel-Liste: annehmen, mit Grund ablehnen oder ignorieren. */
export function pruefeAblage(typen: readonly string[], ziel: FirmenListe): true | string | null {
  const q = quelleAusTypen(typen);
  if (!q) return null;
  const e = ablageFuer(q, ziel);
  if (e.aktion === "keine") return null;
  if (e.aktion === "nichtErlaubt") return e.grund;
  return true;
}

/** Hinweis an der Ziel-Liste, solange etwas Passendes darüber schwebt. */
export const ABLAGE_HINWEIS: Record<FirmenListe, string> = {
  leitung: "Loslassen, um hier als Leitung oder Gesellschafter zu erfassen",
  verantwortung: "Loslassen, um eine Verantwortung fürs Unternehmen zu erfassen",
  mensch: "Loslassen, um als Mensch im Unternehmen zu erfassen",
  verbindung: "Loslassen, um eine Verbindung zu diesem Unternehmen zu erfassen",
};
